/**
 * @fileoverview ReportChart — the one renderer for report-builder results
 * (MOR-906 §7.8): bar / line / stacked-series bar / table, shared by the
 * builder itself and the Analytics favourites board so a saved report looks
 * identical everywhere.
 */

import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
} from "recharts";

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/ui/composed/data-display/chart";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import { formatAud } from "./format";
import { CATEGORY_COLOR_HEX, hexForCategoryColor } from "./categoryVisuals";

export interface ReportRow {
  label: string;
  value: number;
  series?: { name: string; value: number }[];
}

/** Fallback for people / matter series — never brand green. */
const SERIES_COLORS: readonly [string, ...string[]] = [
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
  "#6366f1",
  "#9ca3af",
];

export interface ReportCategorySwatch {
  name: string;
  color?: string;
}

function seriesFill(
  name: string,
  index: number,
  palette: Map<string, string>,
): string {
  if (name === "Other") return CATEGORY_COLOR_HEX.gray;
  return (
    palette.get(name) ??
    SERIES_COLORS[index % SERIES_COLORS.length] ??
    SERIES_COLORS[0]
  );
}

function seriesKey(name: string): string {
  return name.replace(/[^a-zA-Z0-9]/g, "_") || "unknown";
}

export function ReportChart({
  rows,
  seriesNames,
  chart,
  metricLabel,
  groupLabel,
  groupBy,
  categories,
  className,
}: {
  rows: ReportRow[];
  seriesNames?: string[];
  chart: "bar" | "line" | "table";
  metricLabel: string;
  groupLabel: string;
  /** When grouping or stacking by category, bars use each category's colour. */
  groupBy?: string;
  categories?: ReportCategorySwatch[];
  className?: string;
}) {
  const isAmount =
    metricLabel.startsWith("Amount") || metricLabel.startsWith("Written off");
  const fmtValue = (value: number) =>
    isAmount
      ? formatAud(value)
      : value.toLocaleString("en-AU", { maximumFractionDigits: 1 });

  if (rows.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        No time matches this report's blocks in the period.
      </p>
    );
  }

  if (chart === "table" || rows.length > 30) {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{groupLabel}</TableHead>
            {seriesNames?.map((name) => (
              <TableHead key={name} className="text-right">
                {name}
              </TableHead>
            ))}
            <TableHead className="text-right">{metricLabel}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.label}>
              <TableCell className="text-sm">{row.label}</TableCell>
              {seriesNames?.map((name) => (
                <TableCell
                  key={name}
                  className="text-right text-sm tabular-nums"
                >
                  {fmtValue(
                    row.series?.find((s) => s.name === name)?.value ?? 0,
                  )}
                </TableCell>
              ))}
              <TableCell className="text-right text-sm tabular-nums">
                {fmtValue(row.value)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  const stacked = !!seriesNames && seriesNames.length > 0;
  const palette = new Map<string, string>();
  for (const category of categories ?? []) {
    const hex = hexForCategoryColor(category.color);
    if (hex) palette.set(category.name, hex);
  }
  const colorBarsByCategory = !stacked && groupBy === "category";

  const chartData = stacked
    ? rows.map((row) => {
        const flat: Record<string, number | string> = { label: row.label };
        for (const name of seriesNames) {
          flat[seriesKey(name)] =
            row.series?.find((s) => s.name === name)?.value ?? 0;
        }
        return flat;
      })
    : rows;

  const config: ChartConfig = stacked
    ? Object.fromEntries(
        seriesNames.map((name, i) => [
          seriesKey(name),
          {
            label: name,
            color: seriesFill(name, i, palette),
          },
        ]),
      )
    : { value: { label: metricLabel, color: "hsl(var(--primary))" } };

  return (
    <div className={className}>
      <ChartContainer config={config} className="h-72 w-full">
        <ComposedChart data={chartData}>
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis dataKey="label" tickLine={false} axisLine={false} />
          <YAxis
            tickLine={false}
            axisLine={false}
            width={56}
            tickFormatter={(value: number) =>
              isAmount ? formatAud(value) : String(value)
            }
          />
          <ChartTooltip content={<ChartTooltipContent />} />
          {stacked ? (
            seriesNames.map((name, i) => (
              <Bar
                key={name}
                dataKey={seriesKey(name)}
                stackId="series"
                fill={`var(--color-${seriesKey(name)})`}
                radius={i === seriesNames.length - 1 ? [3, 3, 0, 0] : undefined}
              />
            ))
          ) : chart === "line" ? (
            <Line
              dataKey="value"
              stroke="var(--color-value)"
              strokeWidth={2}
              dot={false}
            />
          ) : (
            <Bar
              dataKey="value"
              fill="var(--color-value)"
              radius={[3, 3, 0, 0]}
            >
              {colorBarsByCategory
                ? rows.map((row) => (
                    <Cell
                      key={row.label}
                      fill={seriesFill(row.label, 0, palette)}
                    />
                  ))
                : null}
            </Bar>
          )}
        </ComposedChart>
      </ChartContainer>
      {stacked && (
        <div className="mt-2 flex flex-wrap gap-3">
          {seriesNames.map((name, i) => (
            <span
              key={name}
              className="flex items-center gap-1.5 text-xs text-muted-foreground"
            >
              <span
                className="size-2 rounded-sm"
                style={{
                  backgroundColor: seriesFill(name, i, palette),
                }}
              />
              {name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
